// src/pages/services/StartupMVP.jsx - Premium MVP Development Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Rocket, Lightbulb, Clock, Users, TrendingUp, Target, Layers, Zap } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function StartupMVP() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Lightbulb className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'MVP Strategy & Planning',
      description: 'Define your product vision and create a lean roadmap',
      features: ['Market Research', 'Feature Prioritization', 'User Stories', 'Technical Architecture']
    },
    {
      icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Rapid Prototyping',
      description: 'Get your MVP built in 2-4 weeks',
      features: ['Quick Iterations', 'Lean Development', 'Weekly Demos', 'Fast Launch']
    },
    {
      icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Scalable Architecture',
      description: 'Build on a foundation that grows',
      features: ['Cloud-native', 'Microservices', 'CI/CD Pipeline', 'Performance']
    },
    {
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Technical Co-Founder',
      description: 'Get a dedicated CTO-level expert',
      features: ['Technology Leadership', 'Team Building', 'Investor Meetings', 'Strategy']
    }
  ];

  const whyMVP = [
    { icon: '💰', title: 'Lower Cost', desc: 'Start with minimal investment' },
    { icon: '⚡', title: 'Faster Launch', desc: 'Go to market in weeks' },
    { icon: '📊', title: 'Validate Ideas', desc: 'Test before scaling' },
    { icon: '🎯', title: 'Focus Features', desc: 'Build what users want' },
    { icon: '🔄', title: 'Iterate Quickly', desc: 'Improve based on feedback' },
    { icon: '💡', title: 'Attract Investors', desc: 'Show working product' }
  ];

  const technologies = [
    'React/Next.js', 'Vue.js', 'React Native', 'Flutter',
    'Node.js', 'Python/Django', 'Firebase', 'AWS/GCP',
    'MongoDB', 'PostgreSQL', 'Stripe', 'Twilio'
  ];

  const stats = [
    { value: '2-4 Weeks', label: 'Average MVP Time' },
    { value: '90%', label: 'Success Rate' },
    { value: 'Fast Launch', label: 'Rapid Deployment' },
    { value: 'Proven Process', label: 'Methodology' }
  ];

  const deliverables = [
    'Working MVP Product', 'Source Code & Docs', 'Cloud Deployment', 'Analytics Setup',
    'Admin Dashboard', 'API Documentation', 'QA Report', '3 Months Support'
  ];

  const startupPhases = [
    { phase: 'Idea', desc: 'Concept validation', icon: '💡' },
    { phase: 'MVP', desc: 'Build MVP', icon: '🛠️' },
    { phase: 'Launch', desc: 'Go to market', icon: '🚀' },
    { phase: 'Iterate', desc: 'Improve & feedback', icon: '🔄' },
    { phase: 'Scale', desc: 'Grow users', icon: '📈' },
    { phase: 'Funding', desc: 'Raise rounds', icon: '💰' }
  ];

  const pricingPlans = [
    {
      name: 'Basic MVP',
      price: 749999,
      monthlySupport: 5999,
      color: 'teal',
      badge: null,
      features: [
        'Web App MVP Development',
        '5 Core Features',
        '2-3 Weeks Delivery',
        'Single Platform',
        'Basic Cloud Setup',
        '3 Months Support Included',
        'MVP Documentation'
      ]
    },
    {
      name: 'Pro MVP',
      price: 1499999,
      monthlySupport: 14999,
      color: 'green',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Web + Mobile MVP (iOS/Android)',
        '10+ Core Features',
        '3-4 Weeks Delivery',
        'Scalable Architecture',
        'Advanced Cloud Setup',
        '3 Months Support Included',
        'Investor-Ready Pitch Deck',
        'User Analytics Dashboard'
      ]
    },
    {
      name: 'CTO as Service',
      price: null,
      monthlySupport: 149999,
      color: 'emerald',
      badge: null,
      features: [
        'Dedicated CTO-Level Expert',
        'Full Tech Leadership',
        'Architecture & Strategy',
        'Team Building & Mentoring',
        'Investor Pitch Support',
        'Unlimited Consultation',
        'Technology Roadmap Planning'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-teal-600 via-cyan-600 to-green-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Startup & MVP Development
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-teal-100 mb-6 sm:mb-8 leading-relaxed">
                Turn your startup idea into reality in weeks. Build MVPs fast, validate ideas quickly, and secure funding with our expert guidance.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-teal-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start Your Startup
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Success Stories
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=600&h=400&fit=crop" alt="Startup MVP" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-2xl sm:text-4xl font-black text-teal-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{stat.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white mb-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Startup Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive services to launch your startup successfully
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center text-teal-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MVP Development Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-teal-600/10 via-cyan-600/10 to-green-600/10 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-12 shadow-2xl border border-teal-200/50 dark:border-teal-800/50">
            <Layers className="w-16 h-16 sm:w-24 sm:h-24 text-teal-600 mx-auto mb-4 sm:mb-8 opacity-75 animate-pulse" />
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-teal-600 via-cyan-600 to-green-600 bg-clip-text text-transparent mb-4 sm:mb-6 text-center">
              Build Your MVP for Success
            </h2>
            <p className="text-base sm:text-xl text-gray-700 dark:text-gray-300 mb-6 sm:mb-8 max-w-2xl mx-auto leading-relaxed text-center px-2">
              We design and build MVPs that validate your idea, engage early users, and attract investment. 
              From FinTech to EdTech and HealthTech - 
              <strong className="font-semibold text-teal-600 dark:text-teal-400"> we create custom MVPs to launch your startup successfully</strong>. 
              Let us help you create a strong foundation for your startup journey.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-6 sm:mb-12">
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 rounded-2xl border border-teal-200/50 hover:shadow-2xl transition-all duration-300">
                <Rocket className="w-12 h-12 sm:w-16 sm:h-16 text-teal-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">FinTech MVP</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-teal-600">Secure Transactions</div>
                  <div className="text-base sm:text-lg font-semibold text-cyan-600">Regulatory Compliance</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 rounded-2xl border border-teal-200/50 hover:shadow-2xl transition-all duration-300">
                <Users className="w-12 h-12 sm:w-16 sm:h-16 text-teal-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">EdTech MVP</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-teal-600">Interactive Learning</div>
                  <div className="text-base sm:text-lg font-semibold text-cyan-600">Progress Tracking</div>
                </div>
              </div>
              
              <div className="group p-4 sm:p-8 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 rounded-2xl border border-teal-200/50 hover:shadow-2xl transition-all duration-300">
                <TrendingUp className="w-12 h-12 sm:w-16 sm:h-16 text-teal-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 text-center">HealthTech MVP</h3>
                <div className="space-y-1 sm:space-y-2 text-center">
                  <div className="text-lg sm:text-xl font-bold text-teal-600">Patient Management</div>
                  <div className="text-base sm:text-lg font-semibold text-cyan-600">Health Data Security</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:gap-6">
              <Link to="/contact" className="px-6 sm:px-12 py-3 sm:py-5 bg-gradient-to-r from-teal-600 via-cyan-600 to-green-600 text-white rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 transition-all duration-300 text-center tracking-wide">
                🚀 Start Your MVP Project
              </Link>
              <Link to="/portfolio" className="px-6 sm:px-12 py-3 sm:py-5 border-2 sm:border-4 border-teal-600 text-teal-600 dark:text-teal-400 rounded-2xl font-black text-base sm:text-xl hover:bg-teal-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why MVP */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Why Start with an MVP?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {whyMVP.map((reason, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-4xl sm:text-6xl mb-2 sm:mb-4 group-hover:scale-110 transition-transform">{reason.icon}</div>
                <h3 className="text-base sm:text-xl font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{reason.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            MVP Tech Stack
          </h2>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-gradient-to-r from-teal-500 to-green-500 text-white px-3 sm:px-6 py-1.5 sm:py-3 rounded-full font-semibold text-xs sm:text-base shadow-lg hover:scale-105 transition-all border border-teal-200/50">
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            What You Get
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-5xl mx-auto">
            {deliverables.map((item, index) => (
              <div key={index} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-3 sm:p-6 shadow-lg text-center hover:shadow-xl transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <Check className="text-green-600 mx-auto mb-2 sm:mb-3 w-8 h-8 sm:w-12 sm:h-12 group-hover:scale-110 transition-transform" />
                <p className="text-xs sm:text-base text-gray-900 dark:text-white font-semibold group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Startup Journey */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Your Startup Journey
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {startupPhases.map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-600 to-green-600 text-white rounded-full flex items-center justify-center text-3xl sm:text-4xl mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.phase}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              MVP Development Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for startup MVPs. From basic to CTO-level support.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                teal: { bg: 'from-teal-600 to-cyan-600', light: 'from-teal-50 to-cyan-50', hover: 'hover:border-teal-200' },
                green: { bg: 'from-cyan-600 to-green-600', light: 'from-cyan-50 to-green-50', hover: 'hover:border-green-200' },
                emerald: { bg: 'from-green-600 to-emerald-600', light: 'from-green-50 to-emerald-50', hover: 'hover:border-emerald-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-green-500 dark:border-green-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-cyan-500/10 dark:from-green-600/20 dark:to-cyan-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Basic MVP' && 'Perfect for initial launch'}
                    {plan.name === 'Pro MVP' && 'Best for scalable growth'}
                    {plan.name === 'CTO as Service' && 'Tech leadership & strategy'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'teal' ? '#0d9488, #06b6d4' : plan.color === 'green' ? '#06b6d4, #16a34a' : '#16a34a, #059669'})`}}>
                            ${Math.round(plan.price / 73.5).toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'teal' ? '#0d9488, #06b6d4' : plan.color === 'green' ? '#06b6d4, #16a34a' : '#16a34a, #059669'})`}}>
                            ${Math.round(plan.monthlySupport / 73.5).toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'teal' ? '#0d9488, #06b6d4' : plan.color === 'green' ? '#06b6d4, #16a34a' : '#16a34a, #059669'})`}}>
                          ${Math.round(plan.monthlySupport / 73.5).toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'teal' ? '#ccfbf1' : plan.color === 'green' ? '#d1fae5' : '#d1fae5'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'teal' ? '#0d9488' : plan.color === 'green' ? '#059669' : '#10b981'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'CTO as Service' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '💡', text: 'Product Strategy' },
                { icon: '🚀', text: 'Fast Launch' },
                { icon: '📊', text: 'Analytics Setup' },
                { icon: '📚', text: 'Full Documentation' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-teal-50 to-green-50 dark:from-teal-900/20 dark:to-green-900/20 rounded-2xl border border-teal-200/50 dark:border-teal-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Startup MVP FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How quickly can you build an MVP?',
                  a: 'We deliver MVPs in 2-4 weeks depending on complexity. Basic web apps: 2-3 weeks. Web + mobile: 3-4 weeks. Our rapid development process includes daily standups and weekly demos for transparency.'
                },
                {
                  q: 'What happens after MVP launch?',
                  a: 'We don\'t just build and leave. Your plan includes 3 months of support for bug fixes, improvements, and optimization. We help you gather user feedback and iterate based on real data.'
                },
                {
                  q: 'Can you help with investor pitches?',
                  a: 'Absolutely! We create investor-ready pitch decks, demo videos, and product documentation. Our CTO service includes investor meeting preparation and technical Q&A coaching.'
                },
                {
                  q: 'What\'s included in source code delivery?',
                  a: 'You get complete, production-ready source code with full documentation, architecture guides, deployment instructions, and API documentation. Code is yours to keep and modify.'
                },
                {
                  q: 'Do you help scale after MVP?',
                  a: 'Yes! We build MVPs on scalable architectures (cloud-native, microservices-ready). We also offer ongoing development packages and CTO services for scaling to production.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-teal-600 via-cyan-600 to-green-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Target className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Launch Your Startup?
          </h2>
          <p className="text-base sm:text-xl text-teal-100 mb-6 sm:mb-8 px-2">
            Turn your idea into a funded startup. Book a free consultation today!
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-teal-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>🚀 Schedule Consultation</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="startup-mvp" />
      </div>
    </div>
  );
}